import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { ApiError } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  benvenutoOperatoreAgenzia, creaOperatoreAgenzia, getAgenzieRete, getOperatoriAgenzia, isAccessoSospeso,
  statoOperatoreAgenzia,
} from "@/api/rete";
import type { AgenziaInCarico, OperatoreAgenzia } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";

const CAMPI = [
  { chiave: "nome", etichetta: "Nome", tipo: "text" },
  { chiave: "cognome", etichetta: "Cognome", tipo: "text" },
  { chiave: "email", etichetta: "Email", tipo: "email" },
  { chiave: "cellulare", etichetta: "Cellulare", tipo: "tel" },
] as const;
type Chiave = (typeof CAMPI)[number]["chiave"];
const VUOTO: Record<Chiave, string> = { nome: "", cognome: "", email: "", cellulare: "" };

export function PersoneAgenzia() {
  const { id = "" } = useParams();
  const [agenzia, setAgenzia] = useState<AgenziaInCarico | null>(null);
  const [persone, setPersone] = useState<OperatoreAgenzia[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);
  const [form, setForm] = useState<Record<Chiave, string>>(VUOTO);
  const [inVolo, setInVolo] = useState(false);
  const [esitoForm, setEsitoForm] = useState<string | null>(null);
  const [erroreForm, setErroreForm] = useState<string | null>(null);
  const [esitoRighe, setEsitoRighe] = useState<string | null>(null);
  const [erroreRighe, setErroreRighe] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const seq = useRef(0);

  const carica = useCallback(async () => {
    const mio = ++seq.current;
    setError(null);
    try {
      const ops = await getOperatoriAgenzia(id);
      if (mio !== seq.current) return;
      setPersone(ops);
    } catch (e) {
      if (mio !== seq.current) return;
      if (isAccessoSospeso(e)) setSospeso(true);
      else if (e instanceof ApiError && e.status === 404) setError("Agenzia non in carico.");
      else setError("Impossibile caricare le persone.");
    }
  }, [id]);

  useEffect(() => {
    void carica();
    return () => { seq.current++; };
  }, [carica]);

  // Le agenzie in carico non cambiano da un'azione di riga: si caricano una volta soltanto,
  // separate dalla ricarica delle persone (che invece va ripetuta dopo ogni azione).
  useEffect(() => {
    let cancelled = false;
    getAgenzieRete()
      .then((agenzie) => {
        if (cancelled) return;
        setAgenzia(agenzie.find((a) => a.agenzia_id === id) ?? null);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else if (e instanceof ApiError && e.status === 404) setError("Agenzia non in carico.");
        else setError("Impossibile caricare le persone.");
      });
    return () => { cancelled = true; };
  }, [id]);

  const completo = CAMPI.every((c) => form[c.chiave].trim() !== "");

  async function crea(e: React.FormEvent) {
    e.preventDefault();
    if (!completo) return;
    setInVolo(true);
    setEsitoForm(null);
    setErroreForm(null);
    try {
      const out = await creaOperatoreAgenzia(id, {
        nome: form.nome.trim(), cognome: form.cognome.trim(), email: form.email.trim(), cellulare: form.cellulare.trim(),
      });
      setEsitoForm(out.welcome_inviata
        ? `Operatore creato. Le credenziali sono state inviate a ${form.email.trim()}.`
        : "Operatore creato, ma l'email non è partita: usa «Rimanda benvenuto».");
      setForm(VUOTO);
      await carica();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.detail === "email_gia_usata") {
        setErroreForm("Questa email è già di un altro utente.");
      } else if (err instanceof ApiError && err.status === 404) {
        setErroreForm("Agenzia non in carico.");
      } else {
        setErroreForm("Creazione non riuscita.");
      }
    } finally {
      setInVolo(false);
    }
  }

  async function stato(p: OperatoreAgenzia, attivo: boolean) {
    if (!attivo && !window.confirm(`Disattivare ${p.nome}? Non potrà più entrare nel portale.`)) return;
    setEsitoRighe(null);
    setErroreRighe(null);
    setBusy(p.user_id);
    try {
      await statoOperatoreAgenzia(id, p.user_id, attivo);
      await carica();
    } catch (err) {
      if (isAccessoSospeso(err)) { setSospeso(true); return; }
      setErroreRighe("Aggiornamento non riuscito.");
    } finally {
      setBusy(null);
    }
  }

  async function benvenuto(p: OperatoreAgenzia) {
    if (!window.confirm(`Rimandare il benvenuto a ${p.nome}? La password ricevuta prima non varrà più.`)) return;
    setEsitoRighe(null);
    setErroreRighe(null);
    setBusy(p.user_id);
    try {
      const out = await benvenutoOperatoreAgenzia(id, p.user_id);
      if (out.welcome_inviata) setEsitoRighe(`Benvenuto inviato a ${p.email}.`);
      else setErroreRighe("Invio non riuscito.");
      await carica();
    } catch (err) {
      if (isAccessoSospeso(err)) { setSospeso(true); return; }
      setErroreRighe("Invio non riuscito.");
    } finally {
      setBusy(null);
    }
  }

  if (sospeso) return <AccessoSospeso />;
  if (error) return <ErrorState message={error} />;
  if (persone === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;
  if (agenzia === null) return <ErrorState message="Agenzia non in carico." />;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link to="/rete/agenzie" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-text">
          <ArrowLeft size={16} /> Torna alle agenzie
        </Link>
        <h2 className="text-lg font-semibold text-text">Persone di {agenzia.agenzia_nome}</h2>
      </div>

      <section className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-text">Nuovo operatore</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Riceve per email le credenziali per entrare nel portale.</p>
        </div>
        <form className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" onSubmit={crea}>
          {CAMPI.map((c) => (
            <label key={c.chiave} className="flex flex-col gap-1 text-sm text-text">
              {c.etichetta}
              <Input type={c.tipo} value={form[c.chiave]} autoComplete="off"
                onChange={(e) => setForm({ ...form, [c.chiave]: e.target.value })} />
            </label>
          ))}
          <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
            <Button type="submit" size="sm" disabled={inVolo || !completo}>Crea operatore</Button>
          </div>
        </form>
        {esitoForm && <p className="px-4 pb-3 text-sm text-emerald-700" role="status">{esitoForm}</p>}
        {erroreForm && <p className="px-4 pb-3 text-sm text-destructive" role="alert">{erroreForm}</p>}
      </section>

      <section className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-4 pt-4 pb-3">
          <h3 className="font-semibold text-text">Persone</h3>
          {/* Senza questa riga la colonna «Azioni» vuota su titolari e supervisori
              sembra un guasto: è invece la regola (il responsabile tocca solo gli operatori). */}
          <p className="text-sm text-muted-foreground mt-0.5">
            Puoi creare e gestire gli operatori. Titolari e supervisori li gestisce l'amministrazione.
          </p>
        </div>
        {esitoRighe && <p className="px-4 pb-3 text-sm text-emerald-700" role="status">{esitoRighe}</p>}
        {erroreRighe && <p className="px-4 pb-3 text-sm text-destructive" role="alert">{erroreRighe}</p>}
        {persone.length === 0 ? (
          <div className="px-4 pb-4">
            <EmptyState icon={<Users size={26} className="text-brand" />} title="Nessuna persona in questa agenzia"
              description="Crea il primo operatore con il modulo qui sopra." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead className="hidden md:table-cell">Contatti</TableHead>
                  <TableHead className="hidden md:table-cell">Ruolo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {persone.map((p) => (
                  <TableRow key={p.user_id} className={`[&_td]:py-3 align-top ${p.attivo ? "" : "opacity-60"}`}>
                    <TableCell className="font-medium text-text">
                      {p.nome}
                      {p.username && <div className="text-xs text-muted-foreground font-normal mt-0.5">{p.username}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {p.email ?? "—"}
                      {p.telefono && <div className="text-xs mt-0.5">{p.telefono}</div>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{p.ruoli.join(", ") || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="whitespace-nowrap">{p.attivo ? "Attivo" : "Disattivato"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.solo_operatore ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          {p.attivo && p.mai_entrato && p.email && (
                            <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => benvenuto(p)}>
                              Rimanda benvenuto
                            </Button>
                          )}
                          <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => stato(p, !p.attivo)}>
                            {p.attivo ? "Disattiva" : "Riattiva"}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
